import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  DataTable,
  Dialog,
  Drawer,
  EmptyState,
  Field,
  Icon,
  Select,
  Stat,
  Tag,
  useErrorDialog,
  useToast,
} from '@dayon/design-system';
import { formatCount, formatPesos, formatPesosCompact, formatRate } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { agentPeriodColumns } from './columns.tsx';
import type { AgentDetailPayload, AgentRow, PlanRow } from './types.ts';
import {
  PENDING,
  PLAN_KIND_LABEL,
  PORTAL_STATUS_LABEL,
  SETTLEMENT_LABEL,
  STATUS_LABEL,
  TIER_LABEL,
  bandSentence,
  carrySentence,
  entryRate,
  planFitsTier,
  portalTone,
  retainerSentence,
  stamp,
  statusTone,
  subLine,
  tierTone,
} from './vocabulary.tsx';

/** Which decision the operator is being asked to write down. */
type Decision = 'kyb-pass' | 'kyb-fail' | 'suspend' | 'reinstate' | 'plan';

const DECISION_TITLE: Record<Decision, string> = {
  'kyb-pass': 'Record a KYB pass',
  'kyb-fail': 'Record a KYB failure',
  suspend: 'Suspend this agent',
  reinstate: 'Reinstate this agent',
  plan: 'Change the commission plan',
};

/* ── Agent detail drawer ───────────────────────────────────────────────────
   One drawer for all three row actions. View, Approve and Review are labels
   describing what the agent needs; they are not three different surfaces. */
export function AgentDetail({
  agent,
  settlement,
  canWrite,
  plans,
  onClose,
  onChanged,
  onViewPlayers,
  onPortalAccess,
}: {
  agent: AgentRow;
  settlement: ReactNode;
  canWrite: boolean;
  plans: PlanRow[];
  onClose: () => void;
  onChanged: () => void;
  onViewPlayers: () => void;
  /** Opens the portal-access dialog. Reading the state needs no capability. */
  onPortalAccess: () => void;
}) {
  const { showError } = useErrorDialog();
  const { toast } = useToast();
  const pending = PENDING.has(agent.status);
  const subAgents = Number(agent.sub_agents);

  const [detail, setDetail] = useState<AgentDetailPayload | null>(null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [decision, setDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const [planId, setPlanId] = useState('');
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  /* The per-agent history and the plan it is on. Its own request, so a failure
     here says nothing about the row the list already returned — the drawer
     still shows the agent, and says the history is unread rather than empty. */
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get<AgentDetailPayload>(`/api/admin/agents/${agent.id}/periods`)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setDetailFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setDetail(null);
        setDetailFailed(true);
        showError({
          title: `Could not read ${agent.ref}’s history`,
          message:
            error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [agent.id, agent.ref, showError, reload]);

  const openDecision = useCallback((next: Decision) => {
    setDecision(next);
    setReason('');
    setPlanId('');
    setFields({});
  }, []);

  const eligiblePlans = plans.filter(
    (p) => p.status === 'ACTIVE' && p.brand === agent.brand && planFitsTier(p, agent.tier),
  );

  const submit = async () => {
    if (!decision) return;
    if (reason.trim().length < 8) {
      setFields({ reason: 'Give a reason of at least 8 characters — it is the audit trail.' });
      return;
    }
    if (decision === 'plan' && !planId) {
      setFields({ planId: 'Choose the plan the agent moves to.' });
      return;
    }
    setSaving(true);
    setFields({});
    try {
      if (decision === 'kyb-pass' || decision === 'kyb-fail') {
        const result = await api.post<{ ref: string; status: string }>(
          `/api/admin/agents/${agent.id}/kyb`,
          { outcome: decision === 'kyb-pass' ? 'PASSED' : 'FAILED', reason: reason.trim() },
        );
        toast(
          decision === 'kyb-pass'
            ? `${result.ref} approved — KYB pass recorded, the agent is now active.`
            : `${result.ref} refused — KYB failure recorded, the agent is suspended with the reason attached.`,
        );
      } else if (decision === 'suspend') {
        const result = await api.post<{ ref: string; cascadedSubAgents: number }>(
          `/api/admin/agents/${agent.id}/suspend`,
          { reason: reason.trim() },
        );
        toast(
          result.cascadedSubAgents > 0
            ? `${result.ref} suspended — ${formatCount(result.cascadedSubAgents)} sub-agent${
                result.cascadedSubAgents === 1 ? '' : 's'
              } cascaded with it. Players keep playing.`
            : `${result.ref} suspended. It still accrues commission; it is not paid.`,
        );
      } else if (decision === 'reinstate') {
        const result = await api.post<{ ref: string; liftedCascade: number }>(
          `/api/admin/agents/${agent.id}/reinstate`,
          { reason: reason.trim() },
        );
        toast(
          result.liftedCascade > 0
            ? `${result.ref} reinstated — ${formatCount(result.liftedCascade)} sub-agent${
                result.liftedCascade === 1 ? '' : 's'
              } suspended by the cascade came back with it.`
            : `${result.ref} reinstated.`,
        );
      } else {
        const result = await api.post<{
          ref: string;
          planName: string;
          effectiveFrom: string;
          appliesFromNextPeriod: boolean;
        }>(`/api/admin/agents/${agent.id}/plan`, { planId, reason: reason.trim() });
        toast(
          result.appliesFromNextPeriod
            ? `${result.ref} moves to ${result.planName} on ${stamp(result.effectiveFrom)} — the start of the next settlement period.`
            : `${result.ref} is on ${result.planName} from now: no future settlement period is open, so the change took effect immediately.`,
        );
      }
      setDecision(null);
      setReason('');
      setReload((n) => n + 1);
      onChanged();
    } catch (error: unknown) {
      if (error instanceof ApiRequestError && error.fields) {
        setFields(error.fields);
        return;
      }
      showError({
        title: `${DECISION_TITLE[decision]} — nothing was recorded`,
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'The decision was not written. Try again shortly.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  const plan = detail?.plan ?? null;
  const scheduled = detail?.scheduledPlan ?? null;

  return (
    <>
      <Drawer
        open
        onClose={onClose}
        title={agent.name}
        subtitle={`${agent.ref} · ${agent.parent_ref ? `parent ${agent.parent_ref}` : 'top of tree'} · ${agent.brand}`}
        actions={
          <>
            <Button onClick={onViewPlayers}>View agent&rsquo;s players</Button>
            {/* Offered whatever the state, and to a reader as well as a writer:
                the dialog is where "this agent cannot sign in at all" is said,
                and that is the fact worth reaching even when the operator
                cannot act on it themselves. */}
            <Button onClick={onPortalAccess}>Portal access</Button>
            {canWrite && (
              <>
                {/* Rendered only for a Pending agent — hidden for everyone else
                    rather than disabled, because a greyed Approve on an active
                    agent reads as "approval is stuck". */}
                {pending && (
                  <>
                    <Button onClick={() => openDecision('kyb-fail')}>Refuse KYB</Button>
                    <Button variant="primary" onClick={() => openDecision('kyb-pass')}>
                      Approve agent
                    </Button>
                  </>
                )}
                {agent.status === 'ACTIVE' && (
                  <Button onClick={() => openDecision('suspend')}>Suspend agent</Button>
                )}
                {/* A suspension with no recorded KYB pass is a KYB refusal
                    and is re-decided; one WITH a pass was a commercial or risk
                    decision and comes back through Reinstate. The two are
                    different decisions, and the drawer offers exactly the one
                    the API will accept rather than a button that 409s. */}
                {agent.status === 'SUSPENDED' &&
                  (agent.kyb_completed_at === null ? (
                    <Button variant="primary" onClick={() => openDecision('kyb-pass')}>
                      Record a KYB pass
                    </Button>
                  ) : (
                    <Button variant="primary" onClick={() => openDecision('reinstate')}>
                      Reinstate agent
                    </Button>
                  ))}
                <Button onClick={() => openDecision('plan')}>Change plan</Button>
              </>
            )}
          </>
        }
      >
        <div className="row">
          <Tag tone={tierTone(agent.tier)}>{TIER_LABEL[agent.tier] ?? agent.tier}</Tag>
          <Tag tone={statusTone(agent.status)}>{STATUS_LABEL[agent.status] ?? agent.status}</Tag>
          {agent.area && <Tag tone="quiet">{agent.area}</Tag>}
        </div>

        {/* ── Can they sign in? ─────────────────────────────────────────
            The question this screen could not answer at all until the API grew
            a way to say yes. The chip says "Can sign in" and never "Active",
            because the agent's own status uses that word for something else
            entirely — and INVITED means two different things on the two axes. */}
        <div className="notice notice-quiet">
          <Icon name="key" />
          <div>
            <Tag tone={portalTone(agent.portal_status)}>
              {PORTAL_STATUS_LABEL[agent.portal_status] ?? agent.portal_status}
            </Tag>{' '}
            {agent.portal_status === 'NONE' ? (
              <>
                Nobody at {agent.name} can sign in to the agent portal — there is no login for it.{' '}
                <strong>Portal access</strong> above creates one.
              </>
            ) : agent.portal_status === 'INVITED' ? (
              <>
                {agent.portal_email} was sent a portal invite and has not accepted it, so they
                still cannot sign in. <strong>Portal access</strong> above re-issues the link.
              </>
            ) : (
              <>{agent.portal_email} signs in to the agent portal.</>
            )}
          </div>
        </div>

        <div className="stat-grid">
          <Stat
            label="Players"
            value={formatCount(Number(agent.players))}
            delta="live attribution"
          />
          <Stat label="GGR 30d" value={formatPesosCompact(BigInt(agent.ggr_30d))} />
          <Stat
            label="Commission"
            value={formatPesos(BigInt(agent.commission))}
            delta="accrued, unpaid"
          />
        </div>

        <div className="panel-title">Commission plan</div>
        <dl className="stack" style={{ gap: 'var(--space-2)' }}>
          <div className="def-row">
            <dt>Commission plan</dt>
            {/* The design derives this from the tier alone (Master 5%,
                Sub-agent 4%). The rate is stored per AGENT and the plan is
                stored per ASSIGNMENT, so the drawer prints what this agent is
                actually on — printing the tier default would quietly misstate
                what this one earns. */}
            <dd>
              {detailFailed ? (
                <span className="text-subtle">plan not read</span>
              ) : loading ? (
                <span className="text-subtle">reading…</span>
              ) : plan ? (
                <>
                  {plan.plan_name}{' '}
                  <span className="text-subtle">
                    ({plan.plan_ref} · {PLAN_KIND_LABEL[plan.kind] ?? plan.kind})
                  </span>
                  <div className="text-subtle" style={subLine}>
                    {bandSentence(plan.bands)}
                    {retainerSentence(plan.retainer_amount, plan.settlement)
                      ? ` · plus a ${retainerSentence(plan.retainer_amount, plan.settlement)}`
                      : ''}
                    {plan.bands.length > 1 ? ' · whole amount, not marginal' : ''}
                  </div>
                </>
              ) : (
                <>
                  {TIER_LABEL[agent.tier] ?? agent.tier}{' '}
                  {formatRate(Number(agent.commission_rate_bp))} of net GGR
                  <div className="text-subtle" style={subLine}>
                    on no plan — a rate agreed by hand
                  </div>
                </>
              )}
            </dd>
          </div>
          {scheduled && (
            <div className="def-row">
              <dt>Moves to</dt>
              <dd>
                {scheduled.plan_name} on {stamp(scheduled.effective_from)}
                <div className="text-subtle" style={subLine}>
                  {bandSentence(scheduled.bands)} · not in force yet, so the rate above is still
                  what this agent earns
                </div>
              </dd>
            </div>
          )}
          <div className="def-row">
            <dt>Settlement</dt>
            <dd>
              {plan ? (
                <>
                  {SETTLEMENT_LABEL[plan.settlement] ?? plan.settlement}
                  <div className="text-subtle" style={subLine}>
                    the plan&rsquo;s own rule — the platform&rsquo;s periods are {settlement}
                  </div>
                </>
              ) : (
                settlement
              )}
            </dd>
          </div>
          <div className="def-row">
            <dt>Negative carry-over</dt>
            <dd>
              {plan ? (
                carrySentence(plan.carry_over_months)
              ) : (
                <span className="text-subtle">no term — this agent is on no plan</span>
              )}
            </dd>
          </div>
          <div className="def-row">
            <dt>Sub-agents</dt>
            {/* "2 active" in the design. The count is every sub-agent under this
                parent whatever its status, so it does not claim they are active. */}
            <dd>
              {agent.tier === 'SUB_AGENT'
                ? 'None — a sub-agent has no downline'
                : subAgents === 0
                  ? 'None'
                  : `${formatCount(subAgents)} sub-agent${subAgents === 1 ? '' : 's'}`}
            </dd>
          </div>
          <div className="def-row">
            <dt>KYB</dt>
            <dd>
              {agent.kyb_completed_at ? (
                `Verified ${stamp(agent.kyb_completed_at)}`
              ) : (
                <span className="text-subtle">no outcome recorded</span>
              )}
            </dd>
          </div>
          {agent.status === 'SUSPENDED' && (
            <div className="def-row">
              <dt>Suspended reason</dt>
              <dd>{agent.suspended_reason ?? <span className="text-subtle">none recorded</span>}</dd>
            </div>
          )}
        </dl>

        <div className="notice notice-quiet" id="agent-detail-gap">
          <Icon name="warning-circle" />
          <div>
            <strong>KYB is recorded by hand, not run.</strong> No identity-verification provider is
            integrated with this platform and none is stubbed: an operator holding{' '}
            <strong>partners.manage</strong> reads whatever the business supplied and records the
            outcome with a reason, which is what <strong>Approve agent</strong> and{' '}
            {/* No agent-side document table exists. The roster DOES read ?agentRef= now
                (Players.tsx readFilters), but this button navigates to a bare /players, so the
                old "does not read a filter from the URL yet" was no longer the reason. */}
            <strong>Refuse KYB</strong> write. Nothing records an agent&rsquo;s documents either,
            so a status such as &ldquo;In progress — 2 documents outstanding&rdquo; still has no
            count to print. <strong>View agent&rsquo;s players</strong> opens Player management
            without this agent already chosen — search{' '}
            <span className="tabular">{agent.ref}</span> there, or type that whole code, exactly
            as written, into Agent under More filters.
          </div>
        </div>

        <div className="panel-title">Period history</div>
        {detailFailed ? (
          <EmptyState icon="warning-circle" title="This agent’s history could not be read">
            The commission periods could not be fetched, so this
            table is empty because nothing was read — not because this agent has never settled a
            period.
          </EmptyState>
        ) : loading ? (
          <div className="skeleton" style={{ height: '140px' }}>
            &nbsp;
          </div>
        ) : (detail?.periods.length ?? 0) === 0 ? (
          <EmptyState icon="chart-line" title="No settled periods for this agent">
            Nothing in the commission periods names{' '}
            {agent.ref} yet. A newly created agent has no history until the first period that
            includes it closes.
          </EmptyState>
        ) : (
          <>
            <DataTable
              caption={`Every commission period ${agent.ref} appears in, with the rate it was calculated at, the carry either side of it and the commission`}
              columns={agentPeriodColumns}
              rows={detail!.periods}
              rowKey={(p) => p.id}
            />
            <div className="text-subtle" style={{ fontSize: '11px', marginTop: 'var(--space-2)' }}>
              The <strong>Rate</strong> column is the rate each period was CALCULATED at, frozen on
              the row — not this agent&rsquo;s rate today. A plan change is effective-dated, so the
              two legitimately differ, and printing today&rsquo;s rate over a settled period would
              misstate what was paid. The most recent twelve periods are shown.
            </div>
          </>
        )}
      </Drawer>

      {/* ── One dialog for every decision ─────────────────────────────────
          Each of these is a write with a reason that lands in the audit trail
          in the same transaction as the change. The confirm button stays
          disabled until the reason is real, which is also what keeps a control
          sweep from recording a verdict nobody meant to give. */}
      <Dialog
        open={decision !== null}
        onClose={() => setDecision(null)}
        title={decision ? `${DECISION_TITLE[decision]} · ${agent.ref}` : ''}
        actions={
          <>
            <Button onClick={() => setDecision(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={saving || reason.trim().length < 8}
              onClick={() => void submit()}
            >
              {saving ? 'Recording…' : decision ? DECISION_TITLE[decision] : ''}
            </Button>
          </>
        }
      >
        {decision === 'kyb-pass' && (
          <p className="text-muted" style={{ fontSize: '13px' }}>
            This records a KYB <strong>pass</strong> and makes {agent.ref} active, so it can recruit
            players and accrue commission. Nothing is verified automatically — what you write below
            is the evidence the platform will hold.
          </p>
        )}
        {decision === 'kyb-fail' && (
          <p className="text-muted" style={{ fontSize: '13px' }}>
            This records a KYB <strong>failure</strong>. {agent.ref} is suspended with your reason
            attached; it cannot trade, and the refusal can be re-decided later if the business
            supplies what was missing.
          </p>
        )}
        {decision === 'suspend' && (
          <p className="text-muted" style={{ fontSize: '13px' }}>
            {agent.tier === 'MASTER'
              ? 'Suspending a master cascades to its sub-agents. It never blocks player logins — the players keep playing, and the branch keeps accruing commission it is not paid.'
              : 'A suspended agent still accrues commission; it is held rather than paid. Player logins are untouched.'}
          </p>
        )}
        {decision === 'reinstate' && (
          <p className="text-muted" style={{ fontSize: '13px' }}>
            {agent.tier === 'MASTER'
              ? 'Sub-agents suspended BY THIS MASTER’S cascade come back with it. Any sub-agent suspended on its own account keeps its own reason and stays suspended.'
              : 'The agent returns to active and its held commission resumes being paid.'}
          </p>
        )}
        {decision === 'plan' && (
          <p className="text-muted" style={{ fontSize: '13px' }}>
            The change applies from the <strong>next settlement period</strong> — the date is
            computed from the periods the platform has issued and cannot be chosen, because a date
            inside a settled period would restate what that period paid.
          </p>
        )}

        <div className="stack">
          {decision === 'plan' && (
            <Field
              label="Plan"
              error={fields['planId']}
              hint={
                eligiblePlans.length === 0
                  ? `No active plan on ${agent.brand} is offered to a ${(TIER_LABEL[agent.tier] ?? agent.tier).toLowerCase()}. Create one from Commission plans first.`
                  : 'Only plans on this agent’s brand, offered to its tier, and not withdrawn.'
              }
            >
              {({ id, describedBy }) => (
                <Select
                  id={id}
                  aria-describedby={describedBy}
                  value={planId}
                  onChange={(e) => setPlanId(e.target.value)}
                >
                  <option value="">Choose a plan…</option>
                  {eligiblePlans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.ref} · {p.name} · {bandSentence(p.bands)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}

          {decision === 'plan' &&
            planId &&
            (() => {
              const chosen = eligiblePlans.find((p) => p.id === planId);
              const rate = chosen ? entryRate(chosen.bands) : null;
              if (!chosen || rate === null) return null;
              return (
                <div className="notice notice-quiet">
                  <Icon name="percent" />
                  <div>
                    {agent.ref} carries <strong>{formatRate(rate)}</strong> as its own rate from the
                    effective date — the plan&rsquo;s entry band. The bands above it stay on the
                    plan and are applied by the commission engine, not by this rate.
                    {agent.parent_ref && (
                      <>
                        {' '}
                        A sub-agent&rsquo;s share cannot exceed its master&rsquo;s: the platform
                        refuses the assignment if it would.
                      </>
                    )}
                  </div>
                </div>
              );
            })()}

          <Field
            label="Reason"
            error={fields['reason']}
            hint="At least 8 characters. It is written into the audit trail in the same transaction as the change."
          >
            {({ id, describedBy }) => (
              <textarea
                id={id}
                className="input"
                rows={3}
                aria-describedby={describedBy}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={
                  decision === 'kyb-pass'
                    ? 'SEC registration and beneficial-owner declaration checked against the submitted pack'
                    : decision === 'kyb-fail'
                      ? 'SEC registration number could not be matched to the named entity'
                      : 'Reference the decision this records'
                }
              />
            )}
          </Field>
        </div>
      </Dialog>
    </>
  );
}
